import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { getProfileUpdateError, saveMemberProfileUpdate } from './profileUpdate';

const emptyFields = {
  first_name: '',
  last_name: '',
  phone_number: '',
  email: '',
  firm_address: '',
  employment_category: '',
};

function draftFromMember(member) {
  return {
    profileId: member.id,
    member,
    fields: {
      ...emptyFields,
      first_name: member.first_name || '',
      last_name: member.last_name || '',
      phone_number: member.phone_number || '',
    },
    photoFile: null,
  };
}

export default function UpdateProfile() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, fetchUser } = useAuth();
  const initialDraft = location.state?.profileUpdateDraft;
  const [query, setQuery] = useState(initialDraft?.member?.full_name || '');
  const [results, setResults] = useState([]);
  const [selectedMember, setSelectedMember] = useState(initialDraft?.member || null);
  const [fields, setFields] = useState(initialDraft?.fields || emptyFields);
  const [photoFile, setPhotoFile] = useState(initialDraft?.photoFile || null);
  const [photoPreview, setPhotoPreview] = useState(
    initialDraft?.photoFile
      ? URL.createObjectURL(initialDraft.photoFile)
      : initialDraft?.member?.photo || null
  );
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);
  const [hasSearched, setHasSearched] = useState(Boolean(initialDraft));

  useEffect(() => {
    if (!photoPreview?.startsWith('blob:')) return undefined;
    return () => URL.revokeObjectURL(photoPreview);
  }, [photoPreview]);

  const handleSearch = async (event) => {
    event.preventDefault();
    const searchTerm = query.trim();
    if (searchTerm.length < 2) {
      toast.error('Enter at least 2 characters to search for a member.');
      return;
    }

    setSearching(true);
    setHasSearched(true);
    setSelectedMember(null);
    setResults([]);
    try {
      const response = await api.get(
        `/accounts/find-surveyor/?search=${encodeURIComponent(searchTerm)}`
      );
      const members = response.data.results || response.data || [];
      setResults(members);
      if (members.length === 1) selectMember(members[0]);
    } catch (error) {
      console.error('Member search failed:', error);
      toast.error(getProfileUpdateError(error));
    } finally {
      setSearching(false);
    }
  };

  const selectMember = (member) => {
    const draft = draftFromMember(member);
    setSelectedMember(member);
    setFields(draft.fields);
    setPhotoFile(null);
    setPhotoPreview(member.photo || null);
  };

  const handlePhotoChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file for the passport photo.');
      event.target.value = '';
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Passport photo must be smaller than 5 MB.');
      event.target.value = '';
      return;
    }
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!selectedMember) return;
    const draft = {
      profileId: selectedMember.id,
      member: selectedMember,
      fields,
      photoFile,
    };

    if (!user) {
      navigate('/login', { state: { profileUpdateDraft: draft } });
      return;
    }

    setSaving(true);
    try {
      await saveMemberProfileUpdate(api, draft, user);
      await fetchUser();
      toast.success('The member profile has been updated.');
      navigate(user.role === 'admin' ? '/admin' : '/portal/profile');
    } catch (error) {
      if (error.code === 'PROFILE_MISMATCH') {
        logout();
        navigate('/login', { state: { profileUpdateDraft: draft } });
      }
      toast.error(getProfileUpdateError(error));
    } finally {
      setSaving(false);
    }
  };

  const updateField = (event) => {
    const { name, value } = event.target;
    setFields((current) => ({ ...current, [name]: value }));
  };

  return (
    <main className="min-h-screen bg-[#F8FAFB] py-10 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-[#0B5D1E]">Update Member Information</h1>
          <p className="mt-2 text-gray-600">
            Search for a member, review their details, then sign in as the member or an admin to save the update.
          </p>
        </div>

        <form onSubmit={handleSearch} className="bg-white rounded-xl border shadow-sm p-5 md:p-6">
          <label htmlFor="member-search" className="block text-sm font-semibold text-gray-700 mb-2">
            Search members by name
          </label>
          <div className="flex flex-col sm:flex-row gap-3">
            <input
              id="member-search"
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Enter your first or last name"
              className="flex-1 rounded-lg border border-gray-300 px-4 py-3 focus:border-[#0B5D1E] focus:outline-none focus:ring-2 focus:ring-[#0B5D1E]/20"
            />
            <button
              type="submit"
              disabled={searching}
              className="rounded-lg bg-[#0B5D1E] px-6 py-3 font-semibold text-white hover:bg-[#094A18] disabled:opacity-60"
            >
              {searching ? 'Searching...' : 'Search'}
            </button>
          </div>
        </form>

        {hasSearched && !selectedMember && !searching && (
          <section aria-label="Member search results" className="mt-4 space-y-2">
            {results.length ? results.map((member) => (
              <button
                key={member.id}
                type="button"
                onClick={() => selectMember(member)}
                className="w-full rounded-lg border bg-white p-4 text-left hover:border-[#0B5D1E] hover:shadow-sm"
              >
                <span className="block font-semibold text-gray-900">{member.full_name}</span>
                <span className="text-sm text-gray-500">
                  Membership ID: {member.membership_number || 'Not assigned'}
                </span>
                <span className="sr-only">Select {member.full_name}</span>
              </button>
            )) : (
              <p className="rounded-lg border bg-white p-4 text-sm text-gray-600">
                No member found. Check the spelling and search again.
              </p>
            )}
          </section>
        )}

        {selectedMember && (
          <form onSubmit={handleSubmit} className="mt-6 rounded-xl border bg-white p-5 shadow-sm md:p-8">
            <div className="mb-6 border-b pb-4">
              <h2 className="text-xl font-bold text-gray-900">Confirm or update your details</h2>
              <p className="mt-1 text-sm text-gray-500">
                Selected member: {selectedMember.full_name}.{' '}
                {user?.role === 'admin'
                  ? 'Your admin account can save changes to this member.'
                  : 'Log in with this member account or an admin account to save changes.'}
              </p>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2" htmlFor="passport-photo">
                Passport Photo
              </label>
              <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center">
                {photoPreview ? (
                  <img
                    src={photoPreview}
                    alt="Member portrait preview"
                    className="h-28 w-28 rounded-lg border object-cover"
                  />
                ) : (
                  <div className="flex h-28 w-28 items-center justify-center rounded-lg border bg-gray-50 text-center text-xs text-gray-500">
                    Photo preview
                  </div>
                )}
                <div>
                  <input
                    id="passport-photo"
                    type="file"
                    accept="image/*"
                    required={!photoPreview}
                    onChange={handlePhotoChange}
                    className="block max-w-full text-sm text-gray-600 file:mr-3 file:rounded-md file:border-0 file:bg-[#0B5D1E]/10 file:px-4 file:py-2 file:font-semibold file:text-[#0B5D1E]"
                  />
                  <p className="mt-1 text-xs text-gray-500">Choose an image up to 5 MB. A preview appears before saving.</p>
                </div>
              </div>
            </div>

            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <label htmlFor="first_name" className="mb-1 block text-sm font-semibold text-gray-700">First Name</label>
                <input id="first_name" name="first_name" required value={fields.first_name} onChange={updateField}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2.5 focus:border-[#0B5D1E] focus:outline-none focus:ring-2 focus:ring-[#0B5D1E]/20" />
              </div>
              <div>
                <label htmlFor="last_name" className="mb-1 block text-sm font-semibold text-gray-700">Last Name</label>
                <input id="last_name" name="last_name" required value={fields.last_name} onChange={updateField}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2.5 focus:border-[#0B5D1E] focus:outline-none focus:ring-2 focus:ring-[#0B5D1E]/20" />
              </div>
              <div>
                <label htmlFor="phone_number" className="mb-1 block text-sm font-semibold text-gray-700">Phone</label>
                <input id="phone_number" name="phone_number" type="tel" required value={fields.phone_number} onChange={updateField}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2.5 focus:border-[#0B5D1E] focus:outline-none focus:ring-2 focus:ring-[#0B5D1E]/20" />
              </div>
              <div>
                <label htmlFor="email" className="mb-1 block text-sm font-semibold text-gray-700">Email</label>
                <input id="email" name="email" type="email" required value={fields.email} onChange={updateField}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2.5 focus:border-[#0B5D1E] focus:outline-none focus:ring-2 focus:ring-[#0B5D1E]/20" />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="firm_address" className="mb-1 block text-sm font-semibold text-gray-700">Office Address</label>
                <textarea id="firm_address" name="firm_address" required rows={3} value={fields.firm_address} onChange={updateField}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2.5 focus:border-[#0B5D1E] focus:outline-none focus:ring-2 focus:ring-[#0B5D1E]/20" />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="employment_category" className="mb-1 block text-sm font-semibold text-gray-700">Employment Category</label>
                <select id="employment_category" name="employment_category" required value={fields.employment_category} onChange={updateField}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 focus:border-[#0B5D1E] focus:outline-none focus:ring-2 focus:ring-[#0B5D1E]/20">
                  <option value="">Select employment category</option>
                  <option value="private_sector">Private Sector</option>
                  <option value="public_sector">Public Sector</option>
                </select>
              </div>
            </div>

            <div className="mt-8 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              <button type="button" onClick={() => { setSelectedMember(null); setPhotoFile(null); setPhotoPreview(null); }}
                className="rounded-lg border border-gray-300 px-5 py-3 font-semibold text-gray-700 hover:bg-gray-50">
                Choose another member
              </button>
              <button type="submit" disabled={saving}
                className="rounded-lg bg-[#0B5D1E] px-6 py-3 font-semibold text-white hover:bg-[#094A18] disabled:opacity-60">
                {saving ? 'Saving update...' : user ? 'Save Profile Update' : 'Login to save update'}
              </button>
            </div>
            {!user && (
              <p className="mt-3 text-right text-xs text-gray-500">
                Members can update their own profile. Admins can save updates to any selected member.
              </p>
            )}
          </form>
        )}
      </div>
    </main>
  );
}